import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet,
  ChevronRight, Sparkles, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Input, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// XeroMatchScreen — /ap/xero-match
//
// T-103 — Xero rec catch-up tool. James 2026-04-26.
//
// Use case: James is behind on bank rec. He has 14 unmatched lines in Xero
// Business Account, 19 on the Credit Card. He wants to:
//   1. Export unreconciled bank transactions from Xero → CSV
//   2. Drop the CSV here
//   3. We auto-match against his approved AP invoices on amount + date + supplier
//   4. Per row: confidence-scored suggestion + "Mark as Paid" button
//   5. Optional Xero rec reference input so he can link back later
//
// The actual reconciliation still happens in Xero — this just keeps our pending
// AP queue clean and surfaces what matches what without him spreadsheet-checking
// between two systems. NORMAL workflow stays manual; this is the catch-up tool.
//
// Supports the most common Xero CSV exports:
//   * Bank Statement export (Reconcile → Statement Lines → Export)
//   * Account Transactions report (with Debit/Credit columns)
// Auto-detects column layout by header name.
// =============================================================================

interface BankTxn {
  rowIndex:    number;
  date:        string;       // ISO yyyy-mm-dd
  amount:      number;       // signed, negative = expense
  description: string;
  reference:   string;
}

interface ApCandidate {
  ap_invoice_id:           string;
  ap_number:               string;
  supplier_name:           string | null;
  invoice_date:            string | null;
  total_incl_gst:          number;
  status:                  string;
}

interface MatchResult {
  txn:         BankTxn;
  best:        (ApCandidate & { confidence: number; reasons: string[] }) | null;
}

// -----------------------------------------------------------------------------
// CSV parser — robust enough for Xero exports (handles quoted fields, commas
// inside quotes, CR/LF line endings). No external dep needed.
// -----------------------------------------------------------------------------
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cur += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ",") { row.push(cur); cur = ""; }
      else if (ch === "\r") { /* skip */ }
      else if (ch === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
      else { cur += ch; }
    }
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(cell => cell.trim().length > 0));
}

// Try multiple date formats (Xero uses dd/mm/yyyy or yyyy-mm-dd)
function parseDate(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  // ISO yyyy-mm-dd
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  // dd/mm/yyyy or d/m/yy
  const au = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(t);
  if (au) {
    let yyyy = au[3];
    if (yyyy.length === 2) yyyy = (parseInt(yyyy) > 50 ? "19" : "20") + yyyy;
    return `${yyyy}-${au[2].padStart(2, "0")}-${au[1].padStart(2, "0")}`;
  }
  // dd-MMM-yyyy (e.g. 04-Oct-2025)
  const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const ddMmm = /^(\d{1,2})[-\s]([A-Za-z]{3})[-\s](\d{2,4})$/.exec(t);
  if (ddMmm) {
    const m = months.indexOf(ddMmm[2].toLowerCase());
    if (m >= 0) {
      let yyyy = ddMmm[3];
      if (yyyy.length === 2) yyyy = (parseInt(yyyy) > 50 ? "19" : "20") + yyyy;
      return `${yyyy}-${String(m + 1).padStart(2, "0")}-${ddMmm[1].padStart(2, "0")}`;
    }
  }
  return null;
}

function parseAmount(s: string): number {
  if (!s) return 0;
  const cleaned = s.trim().replace(/[$,\s]/g, "");
  if (!cleaned) return 0;
  // Handle parens for negatives e.g. "(123.45)"
  if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
    return -parseFloat(cleaned.slice(1, -1)) || 0;
  }
  return parseFloat(cleaned) || 0;
}

// Map Xero CSV → BankTxn[]. Auto-detect columns by header.
function csvToTxns(text: string): { txns: BankTxn[]; headers: string[]; warnings: string[] } {
  const rows = parseCsv(text);
  const warnings: string[] = [];
  if (rows.length < 2) return { txns: [], headers: [], warnings: ["Empty or single-row CSV."] };

  const headers = rows[0].map(h => h.trim().toLowerCase());
  const idx = (names: string[]) => {
    for (const n of names) {
      const i = headers.findIndex(h => h === n);
      if (i >= 0) return i;
    }
    return -1;
  };

  const dateIdx = idx(["date", "transaction date", "*date"]);
  const amtIdx  = idx(["amount", "*amount"]);
  const debitIdx  = idx(["debit", "debit amount", "spent", "withdrawal"]);
  const creditIdx = idx(["credit", "credit amount", "received", "deposit"]);
  const descIdx = idx(["description", "narrative", "details", "*description", "payee"]);
  const refIdx  = idx(["reference", "ref", "*reference", "cheque"]);

  if (dateIdx < 0) warnings.push("No 'Date' column found — first column will be tried.");
  if (amtIdx < 0 && debitIdx < 0 && creditIdx < 0) {
    warnings.push("No 'Amount' or 'Debit/Credit' column found.");
  }

  const txns: BankTxn[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const dateStr = row[dateIdx >= 0 ? dateIdx : 0] ?? "";
    const date = parseDate(dateStr);
    if (!date) continue;

    let amount = 0;
    if (amtIdx >= 0) {
      amount = parseAmount(row[amtIdx]);
    } else {
      const debit  = debitIdx >= 0  ? parseAmount(row[debitIdx])  : 0;
      const credit = creditIdx >= 0 ? parseAmount(row[creditIdx]) : 0;
      amount = credit - debit;   // expenses become negative
    }

    txns.push({
      rowIndex:    r,
      date,
      amount,
      description: descIdx >= 0 ? (row[descIdx] ?? "").trim() : "",
      reference:   refIdx  >= 0 ? (row[refIdx]  ?? "").trim() : "",
    });
  }
  return { txns, headers, warnings };
}

// -----------------------------------------------------------------------------
// Match algorithm — confidence-scored
//   amount exact (±$0.01)        +50
//   date within ±5 days          +30
//   supplier name in description +20
// 100 = perfect. Below 50 = no match emitted.
// -----------------------------------------------------------------------------
function matchTxn(txn: BankTxn, candidates: ApCandidate[]): MatchResult["best"] {
  const amtAbs = Math.abs(txn.amount);
  if (amtAbs === 0) return null;

  const txnDateMs = Date.parse(txn.date);
  const descLower = txn.description.toLowerCase();

  let best: MatchResult["best"] = null;
  for (const c of candidates) {
    let score = 0;
    const reasons: string[] = [];

    // Amount match (within 1c)
    const amountDiff = Math.abs(c.total_incl_gst - amtAbs);
    if (amountDiff < 0.01) {
      score += 50;
      reasons.push("amount");
    } else if (amountDiff < 1) {
      score += 25;
      reasons.push("amount~");
    } else {
      continue;  // no amount match, skip
    }

    // Date match (±5 days)
    if (c.invoice_date) {
      const days = Math.abs((txnDateMs - Date.parse(c.invoice_date)) / 86400000);
      if (days <= 5) {
        score += 30;
        reasons.push(`date±${Math.round(days)}d`);
      } else if (days <= 30) {
        score += 10;
      }
    }

    // Supplier name in description
    if (c.supplier_name) {
      const supLower = c.supplier_name.toLowerCase();
      const supTokens = supLower.split(/\s+/).filter(t => t.length >= 4);
      const hit = supTokens.some(tok => descLower.includes(tok));
      if (hit) {
        score += 20;
        reasons.push("supplier");
      }
    }

    if (!best || score > best.confidence) {
      best = { ...c, confidence: score, reasons };
    }
  }
  return best && best.confidence >= 50 ? best : null;
}

function confidenceTone(score: number): "approved" | "pending" | "default" {
  if (score >= 90) return "approved";
  if (score >= 70) return "pending";
  return "default";
}

function confidenceLabel(score: number): string {
  if (score >= 90) return "high";
  if (score >= 70) return "medium";
  return "low";
}

// =============================================================================
// Screen
// =============================================================================

export default function XeroMatchScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canManage = hasRole(user, "manager");

  const [csvText, setCsvText] = useState<string>("");
  const [filename, setFilename] = useState<string>("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pendingPaid, setPendingPaid] = useState<Set<string>>(new Set());
  const [refByApId, setRefByApId] = useState<Record<string, string>>({});

  // Pull approved + pending_review AP invoices that haven't been paid
  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["xero_match_candidates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ap_invoices")
        // T-397 — real columns are invoice_number + amount_inc_gst (ap_number and
        // total_incl_gst never existed on ap_invoices → candidate list was empty).
        // Internal field names kept so the matcher below doesn't churn.
        .select(`
          ap_invoice_id, invoice_number, invoice_date, amount_inc_gst, status,
          supplier_id, supplier_name_raw,
          supplier:supplier_id (legal_name, trading_name)
        `)
        // Live enum values (verified 2026-04-26):
        //   captured / pending_review / approved / entered_in_xero / paid / disputed / void
        // We want anything past initial capture but not yet paid.
        .in("status", ["approved", "pending_review", "entered_in_xero"])
        .is("paid_date", null);
      if (error) throw error;
      return ((data ?? []) as any[]).map(r => ({
        ap_invoice_id:   r.ap_invoice_id,
        ap_number:       r.invoice_number,
        supplier_name:   r.supplier?.legal_name ?? r.supplier?.trading_name ?? r.supplier_name_raw ?? null,
        invoice_date:    r.invoice_date,
        total_incl_gst:  Number(r.amount_inc_gst ?? 0),
        status:          r.status,
      })) as ApCandidate[];
    },
  });

  const parsed = useMemo(() => csvText ? csvToTxns(csvText) : null, [csvText]);
  const txns = parsed?.txns ?? [];

  const matches = useMemo<MatchResult[]>(() => {
    if (!txns.length || !candidates.length) return txns.map(t => ({ txn: t, best: null }));
    // Track which candidates have been claimed so we don't double-match
    const claimed = new Set<string>();
    const sorted = [...txns].sort((a, b) => a.rowIndex - b.rowIndex);
    const results: MatchResult[] = [];
    for (const t of sorted) {
      const available = candidates.filter(c => !claimed.has(c.ap_invoice_id));
      const best = matchTxn(t, available);
      if (best) claimed.add(best.ap_invoice_id);
      results.push({ txn: t, best });
    }
    return results;
  }, [txns, candidates]);

  const matchSummary = useMemo(() => {
    let high = 0, med = 0, low = 0, none = 0;
    for (const m of matches) {
      if (!m.best) none++;
      else if (m.best.confidence >= 90) high++;
      else if (m.best.confidence >= 70) med++;
      else low++;
    }
    return { high, med, low, none, total: matches.length };
  }, [matches]);

  const markPaidMut = useMutation({
    mutationFn: async (args: { apId: string; paidAt: string; ref: string | null }) => {
      const { error } = await supabase
        .from("ap_invoices")
        .update({
          status: "paid",
          paid_date: args.paidAt,           // live schema uses paid_date (not paid_at)
          payment_reference: args.ref,
        })
        .eq("ap_invoice_id", args.apId);
      if (error) {
        const parts = [error.message, (error as any).details, (error as any).hint].filter(Boolean);
        throw new Error(parts.join(" — "));
      }
    },
    onSuccess: (_data, vars) => {
      setPendingPaid(prev => new Set([...prev, vars.apId]));
      qc.invalidateQueries({ queryKey: ["xero_match_candidates"] });
      qc.invalidateQueries({ queryKey: ["ap_invoices"] });
    },
    // T-403 — this was silent (the T-380 bug shape): a failed mark-paid left
    // the row looking untouched with no message, and bulkMarkHigh could
    // silently no-op a whole batch.
    onError: (err: any, vars) => {
      alert(`Mark-paid failed for one bill (${vars.apId.slice(0, 8)}…): ${err?.message ?? err}`);
    },
  });

  const bulkMarkHigh = async () => {
    const high = matches.filter(m => m.best && m.best.confidence >= 90 && !pendingPaid.has(m.best.ap_invoice_id));
    if (high.length === 0) return;
    if (!window.confirm(`Mark ${high.length} high-confidence matches as paid?`)) return;
    let ok = 0, failed = 0;
    for (const m of high) {
      try {
        await markPaidMut.mutateAsync({
          apId:   m.best!.ap_invoice_id,
          paidAt: m.txn.date,
          ref:    refByApId[m.best!.ap_invoice_id] || null,
        });
        ok++;
      } catch {
        failed++;   // per-item alert already fired via onError
      }
    }
    if (failed > 0) alert(`Bulk mark-paid: ${ok} succeeded, ${failed} FAILED — the failed ones are still unpaid.`);
  };

  function onFilePicked(file: File | null) {
    if (!file) return;
    setFilename(file.name);
    file.text().then(text => {
      setCsvText(text);
      const result = csvToTxns(text);
      setWarnings(result.warnings);
    });
  }

  function reset() {
    setCsvText("");
    setFilename("");
    setWarnings([]);
    setPendingPaid(new Set());
    setRefByApId({});
  }

  if (!canManage) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-text-muted">
            Manager role required to use the Xero rec catch-up tool.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-20">
      <Link to="/finance" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Finance hub
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Xero rec catch-up</h1>
          <p className="text-text-muted text-[13px] mt-1">
            Drop your unreconciled Xero bank-statement CSV. We'll auto-match it against approved AP invoices that haven't been paid yet, so you can mark them paid in our system in one pass.
          </p>
        </div>
        {csvText && (
          <Button variant="secondary" icon={X} onClick={reset}>Reset</Button>
        )}
      </div>

      {!csvText ? (
        <Card>
          <div className="p-8">
            <label className="block border-2 border-dashed border-line rounded-lg p-10 text-center cursor-pointer hover:border-accent transition-colors"
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }} onDragEnter={e => e.preventDefault()} 
              onDrop={e => { e.preventDefault(); onFilePicked(e.dataTransfer.files?.[0] ?? null); }}>
              <Upload size={32} className="mx-auto text-text-muted mb-3" />
              <div className="text-text font-medium">Drop your Xero CSV here</div>
              <div className="text-text-muted text-[12px] mt-1">
                Export from Xero → Reconcile screen → Statement Lines tab → Export, or any Account Transactions report CSV.
              </div>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={e => onFilePicked(e.target.files?.[0] ?? null)}
              />
            </label>
            <div className="mt-4 text-[11px] font-mono text-text-dim">
              We auto-detect columns. Required: Date + Amount (or Debit/Credit). Optional: Description, Reference.
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Card><div className="p-3"><div className="text-[10px] uppercase tracking-wider2 font-mono text-text-muted">File</div><div className="text-[13px] text-text font-medium truncate mt-1">{filename}</div></div></Card>
            <Card><div className="p-3"><div className="text-[10px] uppercase tracking-wider2 font-mono text-text-muted">Lines</div><div className="text-[20px] font-mono text-text mt-1">{matchSummary.total}</div></div></Card>
            <Card><div className="p-3"><div className="text-[10px] uppercase tracking-wider2 font-mono text-text-muted">High match</div><div className="text-[20px] font-mono text-ok mt-1">{matchSummary.high}</div></div></Card>
            <Card><div className="p-3"><div className="text-[10px] uppercase tracking-wider2 font-mono text-text-muted">Medium / Low</div><div className="text-[20px] font-mono text-warn mt-1">{matchSummary.med + matchSummary.low}</div></div></Card>
            <Card><div className="p-3"><div className="text-[10px] uppercase tracking-wider2 font-mono text-text-muted">No match</div><div className="text-[20px] font-mono text-text-muted mt-1">{matchSummary.none}</div></div></Card>
          </div>

          {warnings.length > 0 && (
            <div className="mb-4 bg-warn/10 border border-warn/30 rounded p-3 text-[12px] text-warn">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-medium mb-1">CSV parser notes:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {matchSummary.high > 0 && (
            <div className="mb-4">
              <Button icon={Sparkles} onClick={bulkMarkHigh} disabled={markPaidMut.isPending}>
                {markPaidMut.isPending ? "Marking…" : `Mark all ${matchSummary.high} high-confidence as paid`}
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : (
            <Card>
              <div className="divide-y divide-line">
                <div className="grid grid-cols-[100px_1fr_120px_2fr_140px_140px] gap-3 px-3 py-2 text-[10px] font-medium text-text-muted uppercase tracking-wider2 bg-ink-raised">
                  <div>Bank date</div>
                  <div>Bank description</div>
                  <div className="text-right">Amount</div>
                  <div>Suggested AP match</div>
                  <div>Xero rec ref</div>
                  <div></div>
                </div>
                {matches.map((m, i) => {
                  const isPaid = m.best ? pendingPaid.has(m.best.ap_invoice_id) : false;
                  return (
                    <div key={i} className={`grid grid-cols-[100px_1fr_120px_2fr_140px_140px] gap-3 px-3 py-3 items-center text-[12px] ${isPaid ? "bg-ok/5 opacity-70" : ""}`}>
                      <div className="font-mono text-text-muted">{fmtDate(m.txn.date)}</div>
                      <div className="text-text truncate" title={m.txn.description}>{m.txn.description || <span className="text-text-dim">(no description)</span>}</div>
                      <div className={`font-mono text-right ${m.txn.amount < 0 ? "text-text" : "text-ok"}`}>{fmtMoney(Math.abs(m.txn.amount))}{m.txn.amount < 0 ? "" : " IN"}</div>
                      <div className="min-w-0">
                        {m.best ? (
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-[11px] text-accent">{m.best.ap_number}</span>
                              <Pill label={confidenceLabel(m.best.confidence)} tone={confidenceTone(m.best.confidence)} small/>
                              <span className="text-[10px] font-mono text-text-dim">{m.best.reasons.join(" + ")}</span>
                            </div>
                            <div className="text-text-muted text-[11px] truncate mt-0.5">
                              {m.best.supplier_name ?? "—"} · {fmtMoney(m.best.total_incl_gst)} · {m.best.invoice_date ? fmtDate(m.best.invoice_date) : "no date"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-text-dim italic">No AP match found</span>
                        )}
                      </div>
                      <div>
                        {m.best && !isPaid && (
                          <Input
                            mono
                            placeholder="optional"
                            value={refByApId[m.best.ap_invoice_id] ?? ""}
                            onChange={e => setRefByApId(prev => ({ ...prev, [m.best!.ap_invoice_id]: e.target.value }))}
                            className="text-[11px]"
                          />
                        )}
                        {isPaid && refByApId[m.best!.ap_invoice_id] && (
                          <span className="text-[11px] font-mono text-text-muted">{refByApId[m.best!.ap_invoice_id]}</span>
                        )}
                      </div>
                      <div className="text-right">
                        {isPaid ? (
                          <span className="inline-flex items-center gap-1 text-[12px] text-ok"><CheckCircle2 size={12}/> Marked paid</span>
                        ) : m.best ? (
                          <Button
                            variant={m.best.confidence >= 90 ? "primary" : "secondary"}
                            onClick={() => markPaidMut.mutate({
                              apId:   m.best!.ap_invoice_id,
                              paidAt: m.txn.date,
                              ref:    refByApId[m.best!.ap_invoice_id] || null,
                            })}
                            disabled={markPaidMut.isPending}
                          >
                            Mark Paid
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {matchSummary.none > 0 && (
            <div className="mt-4 text-[12px] text-text-muted">
              <strong>{matchSummary.none}</strong> bank lines didn't match any approved AP invoice in our system. Check that the supplier invoice is loaded into <Link to="/ap" className="text-accent">/ap</Link> and approved (status = approved or pushed_to_xero).
            </div>
          )}
        </>
      )}
    </div>
  );
}
